import { XY } from "./vendor/sailcloth/sailcloth";
import debounce from "debounce";
// this function will pop n values off the array 'a' and
// return the resulting array. If there are less than 'n'
// items in the array, then the whole array will be poped
const array_pop_n = function (a, n) {
    const result = [];
    for (let i = 0; i < n && a.length > 0; i++) {
        result.push(a.pop());
    }
    return result;
};
export class BoardDataSource {
    constructor(options) {
        this.options = options || {};
        this.data = {};
        this.tileBlockQueue = [];
        this.storedTileBlocks = {};
        this.bonusTiles = {};
        this.pendingTiles = {};
        this.letterValues = this.options.letterValues || {};
        if (!this.options.tileBlockSize) {
            throw "tileBlockSize required to create BoardDataSource";
        }
        if (!this.options.socket) {
            throw "socket required";
        }
        this.options.onDataChanged = this.options.onDataChanged || function () { };
        this.tileBlockSize = this.options.tileBlockSize;
        this.socket = this.options.socket;
        this.socket.addEvent("tile_data", data => this.onDataArrived(data));
        this.socket.on("tile_change", (data) => {
            data.moves.forEach(move => {
                this.set(move.bx, move.by, move.letter);
            });
            if (this.options.onTilesPlayed) {
                this.options.onTilesPlayed(data, this);
            }
            this.onChange();
        });
        this.triggerTileBlockRequest = debounce(() => this.requestQueuedTileBlocks(), 80);
    }
    // clear the storedTileBlocks object, but *don't* clear any new data
    // this will cause all the data to be requested again from the server
    // but in such a way that the user shouldn't notice
    invalidateAll() {
        this.storedTileBlocks = {};
    }
    onDataArrived(response) {
        for (const tile of response) {
            this.processIncomingTile(tile);
        }
        this.onChange();
    }
    processIncomingTile(tile) {
        if (!tile) {
            return;
        }
        const rect = this.rectForTile(tile.tx, tile.ty);
        tile.tile_data.split("").forEach((c, i) => {
            if (c && c != ".") {
                const bx = (i % this.tileBlockSize) + rect.bounds.left;
                const by = Math.floor(i / this.tileBlockSize) + rect.bounds.top;
                this.set(bx, by, c);
            }
        });
        if (tile.bonuses) {
            const bonusTypes = ["x2", "x3"];
            for (const bt of bonusTypes) {
                tile.bonuses[bt].forEach((b) => {
                    const x = b[0];
                    const y = b[1];
                    const vx = x + rect.bounds.left;
                    const vy = y + rect.bounds.top;
                    const k = `${vx}:${vy}`;
                    this.bonusTiles[k] = bt;
                });
            }
        }
    }
    rectForTile(tx, ty) {
        return new XY.Rect(tx * this.tileBlockSize, ty * this.tileBlockSize, this.tileBlockSize, this.tileBlockSize);
    }
    tileBlockForLocation(bx, by) {
        return {
            tx: Math.floor(bx / this.tileBlockSize),
            ty: Math.floor(by / this.tileBlockSize),
        };
    }
    hasTileBlock(tx, ty) {
        return !!this.storedTileBlocks[this.key(tx, ty)];
    }
    haveDataFor(bx, by) {
        const p = this.tileBlockForLocation(bx, by);
        return this.hasTileBlock(p.tx, p.ty);
    }
    loadData(tx, ty) {
        this.storedTileBlocks[this.key(tx, ty)] = true;
        this.tileBlockQueue.push({ tx: tx, ty: ty });
        // this method is debounced, so it will only request the data
        // after a certain delay, in order to minimise the number of
        // requests
        this.triggerTileBlockRequest();
    }
    requestQueuedTileBlocks() {
        this.socket.whenConnected().then(ws => {
            if (this.tileBlockQueue.length == 0) {
                return;
            }
            // there is a maximum number of tiles that can be requested at once
            // both to stop abuse of the server, and to keep the packet size low
            // if we need more tiles than that, then another request will be made
            const tiles = array_pop_n(this.tileBlockQueue, 16);
            ws.send("tiles_needed", tiles);
            // the number of required was greater than the maximum request number
            // so we trigger another request for tile data, because this method is
            // debounced there will be another delay
            if (this.tileBlockQueue.length > 0) {
                this.triggerTileBlockRequest();
            }
        });
    }
    letterValue(letter) {
        return this.letterValues[letter.toLowerCase()] || 0;
    }
    get(bx, by) {
        if (this.haveDataFor(bx, by)) {
            const k = this.key(bx, by);
            return this.data[k];
        }
        else {
            const p = this.tileBlockForLocation(bx, by);
            this.loadData(p.tx, p.ty);
        }
    }
    // returns a promise that will resolve or reject if the
    // move is accepted by the server
    commitPending() {
        if (!this.socket.isConnected()) {
            return Promise.reject("not_connected");
        }
        const pending = Object.values(this.pendingTiles);
        return this.socket.send("commit_move", pending).then(response => {
            if (response.status == "ok") {
                this.pendingTiles = {};
                response.moves = response.moves || pending;
                return response;
            }
            else {
                return Promise.reject(response);
            }
        });
    }
    get pendingCount() {
        return Object.keys(this.pendingTiles || {}).length;
    }
    hasPending() {
        return this.pendingCount > 0;
    }
    isPending(bx, by) {
        return !!this.pendingTiles[this.key(bx, by)];
    }
    // will return false if that tile is already taken
    setPending(bx, by, letter) {
        if (this.get(bx, by)) {
            return false;
        }
        // we set it normally and then add it to the pending list
        this.set(bx, by, letter);
        this.pendingTiles[this.key(bx, by)] = { bx: bx, by: by, letter: letter };
        this.onChange();
        return true;
    }
    clearPending(bx, by) {
        const k = this.key(bx, by);
        const pending = this.pendingTiles[k];
        if (pending) {
            delete this.pendingTiles[k];
            return pending.letter;
        }
        else {
            return null;
        }
    }
    moveLetter(from, to) {
        if (from.x == to.x && from.y == to.y) {
            // if the from and to are the same, then we don't
            // need to do anything
            return from;
        }
        if (this.isSet(to.x, to.y)) {
            // if the destination already has a letter on it
            // then we do nothing as well
            return from;
        }
        if (!this.isPending(from.x, from.y)) {
            // only pending tiles can move, so if its not pending,
            // just return...
            return from;
        }
        const letter = this.get(from.x, from.y);
        this.reset(from.x, from.y);
        this.setPending(to.x, to.y, letter || ".");
        return to;
    }
    reset(bx, by) {
        const k = this.key(bx, by);
        if (this.isPending(bx, by)) {
            delete this.pendingTiles[k];
        }
        if (this.isSet(bx, by)) {
            delete this.data[k];
        }
    }
    resetPending() {
        Object.keys(this.pendingTiles).forEach(k => (this.data[k] = null));
        const a = this.pendingTiles;
        this.pendingTiles = {};
        this.onChange();
        return Object.values(a).map(t => t.letter);
    }
    onChange() {
        if (this.options.onDataChanged) {
            this.options.onDataChanged(this);
        }
    }
    isSet(bx, by) {
        return !!this.get(bx, by);
    }
    set(bx, by, letter) {
        this.data[this.key(bx, by)] = letter;
    }
    key(bx, by) {
        return `${bx}:${by}`;
    }
}
